import {inject, Injectable} from "@angular/core";
import {VatRate} from "../libs/enum/vat-rate.enum";
import {VatRateService} from "./vat-rate.service";
import {VatRateDto} from "../repositories/dto/vat-rate.dto";
import {isDefined, safeSplit} from "../libs/utils/epic.utils";
import {VatRateState} from "../libs/enum/vat-rate-state";

@Injectable({providedIn: 'root'})
export class CalcService {

  public static readonly DEFAULT_FRACTION_DIGITS: number = 5;
  public static readonly MIN_SCALE: number = 2;

  private readonly vatRateService: VatRateService = inject(VatRateService);

  private fractionDigits: number;
  private vatRates: VatRateDto[] = [];

  constructor() {
    this.fractionDigits = CalcService.DEFAULT_FRACTION_DIGITS;
    this.vatRateService.getAll().subscribe((vrs) => this.vatRates = vrs);
  }

  public setDefaultFractionDigits(): void {
    this.setFractionDigits(CalcService.DEFAULT_FRACTION_DIGITS);
  }

  public netAmountByGrossAmountAndVatRate(grossAmount: number, vatRate: VatRate, date?: Date): number {
    return this.netAmountByGrossAmountAndVatRateAndQuantity(grossAmount, vatRate, 1, date);
  }

  public setFractionDigits(digits: number): void {
    this.fractionDigits = digits;
  }

  public netAmountByGrossAmountAndVatRateAndQuantity(grossAmount: number, vatRate: VatRate, quantity: number, date?: Date): number {
    const dto: VatRateDto = this.getVatRateDtoByVatRate(vatRate, date);
    return this.netAmountByGrossAmountAndVatRatePercentAndQuantity(grossAmount, dto.value, quantity);
  }

  public netAmountByGrossAmountAndVatRatePercentAndQuantity(grossAmount: number, vatRatePercent: number, quantity: number): number {
    const vatRateDecimal: number = this.vatRateDecimal(vatRatePercent);
    return this.toFixed((grossAmount / vatRateDecimal) * quantity);
  }

  public grossAmountByNetAmountAndVatRate(netAmount: number, vatRate: VatRate, date?: Date): number {
    return this.grossAmountByNetAmountAndVatRateAndQuantity(netAmount, vatRate, 1, date);
  }

  public grossAmountByNetAmountAndVatRateAndQuantity(netAmount: number, vatRate: VatRate, quantity: number, date?: Date): number {
    const dto: VatRateDto = this.getVatRateDtoByVatRate(vatRate, date);
    return this.grossAmountByNetAmountAndVatRatePercentAndQuantity(netAmount, dto.value, quantity);
  }

  public grossAmountByNetAmountAndVatRatePercentAndQuantity(netAmount: number, vatRatePercent: number, quantity: number): number {
    const vatRateDecimal: number = this.vatRateDecimal(vatRatePercent);
    return this.toFixed(netAmount * vatRateDecimal * quantity);
  }

  public vatAmountByGrossAmountAndNetAmount(grossAmount: number, netAmount: number): number {
    return this.toFixed(grossAmount - netAmount);
  }

  // noinspection JSUnusedGlobalSymbols
  public vatAmountByGrossAmountAndNetAmountAndQuantity(grossAmount: number, netAmount: number, quantity: number): number {
    return this.toFixed((grossAmount - netAmount) * quantity);
  }

  public subtract(minuend: number | string, subtrahend: number | string, fractionDigits?: number): number {
    const m: number = typeof minuend === 'string' ? this.asNumber(minuend) : minuend;
    const s: number = typeof subtrahend === 'string' ? this.asNumber(subtrahend) : subtrahend;
    return this.toFixed(m - s, fractionDigits);
  }

  /**
   * Prozentwert Berechnung
   * Formel: "W = p * G / 100" or "W = (G / 100) * p"
   */
  public percentage(base: number, percent: number, fractionDigits?: number): number {
    const percentage: number = percent * base / 100;
    return this.toFixed(percentage, fractionDigits);
  }

  /**
   * Prozentsaetze Berechnen
   * Formel: "p% = W / G" or "p = W / G * 100"
   * @param base G (Grundwert)
   * @param percentage W (Prozentwert)
   * @param fractionDigits
   */
  // noinspection JSUnusedGlobalSymbols
  public percentOf(base: number, percentage: number, fractionDigits?: number): number {
    const p: number = percentage / base * 100;
    return this.toFixed(p, fractionDigits);
  }

  public toFixed(value: number | string, fractionDigits?: number): number {
    if (value || value === 0) {
      const fd: number = fractionDigits || fractionDigits === 0 ? fractionDigits : this.fractionDigits;
      const useValue: number = typeof value === 'string' ? this.asNumber(value) : value;
      return parseFloat(useValue.toFixed(fd));
    }
    return this.asNumber(value);
  }

  private asNumber(value: string | number): number {
    return parseFloat(this.asString(value));
  }

  public asString(value: string | number, fractionDigits?: number): string {
    const digits: number = fractionDigits ? fractionDigits : this.fractionDigits;
    return parseFloat(String(value)).toFixed(digits);
  }

  public getFractionDigits(value: number): number {
    const numString: string = value.toString();
    return numString.includes('.') ? safeSplit(numString, '.')[1].length : 0;
  }

  public getFractionDigitsInRange(value: number): number {
    return Math.max(2, Math.min(5, this.getFractionDigits(value)));
  }

  public getVatRateDtoByVatRate(vatRate: VatRate, date?: Date): VatRateDto {
    if (isDefined(date)) {
      const time: number = date.getTime();
      const dto: VatRateDto | undefined = this.vatRates
        .filter((vr) => isDefined(vr.validFrom) && isDefined(vr.validUntil))
        .filter((vr) => vr.validFrom!.getTime() <= time && time <= vr.validUntil!.getTime())
        .find((vr) => vr.vatRate === vatRate);
      if (isDefined(dto)) {
        return dto;
      }
    }
    return this.vatRates
      .filter((vr) => vr.state === VatRateState.CURRENT)
      .find((vr) => vr.vatRate === vatRate)!;
  }

  private rateDecimal(ratePercent: number): number {
    return (100 + ratePercent) / 100;
  }

  private vatRateDecimal(vatRatePercent: number): number {
    return this.rateDecimal(vatRatePercent);
  }
}
